import { useCallback, useState } from "react";
import { useMultiSiteContext } from "@hooks/use-site-context";
import { useScreenAlert } from "@hooks/use-screen-alert";
import { useUserContext } from "@hooks/use-auth-context";
import { useRefreshTokenMutation } from "@hooks/use-refresh-token-mutation";
import { fetchWithTokenRefresh } from "@hooks/shared/fetch-with-token-refresh";
import { addQueryArgs } from "@wordpress/url";
import { sanitizeInput, isValidColumnName, isValidFilterLength } from "../utils";
import type { SiteUser } from "./types";

const SITE_USERS_ENDPOINT = "wp-json/smassets/v1/site-users";
const SITE_USERS_SEARCH_ENDPOINT = "wp-json/smassets/v1/site-users/search";

/** Only internal DB identity fields - never expose as filter query params. */
const BLOCKED_FILTER_COLUMNS = new Set(["wp_user_id", "id", "blog_id"]);

export const useGetUsers = () => {
  const [users, setUsers] = useState<SiteUser[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { setError: setScreenError } = useScreenAlert();
  const { user } = useUserContext();
  const { refreshUserToken } = useRefreshTokenMutation();
  const { getApiUrl, blogId } = useMultiSiteContext();

  const getUsers = useCallback(
    async ({
      filters = [],
      searchValue = "",
    }: { filters?: any[]; searchValue?: string } = {}) => {
      setLoading(true);
      setError(null);

      try {
        const perPageFilter = filters.find((f) => f.column === "per_page");
        const pageFilter = filters.find((f) => f.column === "page");
        const otherFilters = filters.filter(
          (f) => f.column !== "per_page" && f.column !== "page"
        );

        const perPage: number = perPageFilter?.value ?? 50;
        const page: number = pageFilter?.value ?? 1;

        const queryParams: Record<string, string | number> = {
          per_page: perPage,
          page,
          ...(blogId !== null ? { blog_id: blogId } : {}),
        };

        let url: string;

        if (searchValue && searchValue.length >= 2) {
          url = getApiUrl(SITE_USERS_SEARCH_ENDPOINT);
          queryParams.q = sanitizeInput(searchValue);
          queryParams.limit = perPage;
        } else {
          url = getApiUrl(SITE_USERS_ENDPOINT);
          for (const filter of otherFilters) {
            const col = filter.column as string;
            if (BLOCKED_FILTER_COLUMNS.has(col)) continue;
            if (!isValidColumnName(col)) continue;
            const raw = String(filter.value ?? "");
            if (!isValidFilterLength(raw)) continue;
            queryParams[col] = sanitizeInput(raw);
          }
        }

        const response = await fetchWithTokenRefresh(
          addQueryArgs(url, queryParams as Record<string, any>),
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user?.authToken ?? ""}`,
            },
          },
          () => refreshUserToken(user?.refreshToken ?? "")
        );

        if (!response.ok) {
          throw new Error(
            `Failed to fetch users: ${response.status} ${response.statusText}`
          );
        }

        const data: { items: SiteUser[]; total: number; pages?: number } =
          await response.json();

        setUsers(data.items);
        setTotal(data.total);
        setLoading(false);
        return { items: data.items, total: data.total };
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Unknown error fetching users";
        setError(errorMessage);
        setScreenError(errorMessage);
        setLoading(false);
        return { items: [], total: 0 };
      }
    },
    [getApiUrl, blogId, user?.authToken, user?.refreshToken, refreshUserToken, setScreenError]
  );

  const getSiteUserById = useCallback(
    async (wpUserId: number): Promise<SiteUser | null> => {
      try {
        const url = getApiUrl(`${SITE_USERS_ENDPOINT}/${wpUserId}`);
        const response = await fetchWithTokenRefresh(
          url,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user?.authToken ?? ""}`,
            },
          },
          () => refreshUserToken(user?.refreshToken ?? "")
        );
        if (!response.ok) return null;
        return (await response.json()) as SiteUser;
      } catch {
        return null;
      }
    },
    [getApiUrl, user?.authToken, user?.refreshToken, refreshUserToken]
  );

  return {
    users,
    total,
    loading,
    error,
    getUsers,
    getSiteUserById,
  };
};
